import { UserProfile } from 'fitbeat.gym-core';
import { UserNotification } from 'fitbeat.models';
import { BookingServiceClient, UserServiceClient } from 'fitbeat.api-clients';
import { Platform } from 'react-native';

import Storage from '../utils/Storage';
import { getApiConfig } from '../config';
import { AuthTokenBuilder } from '../utils/authTokenBuilder';
import { GoogleAnalytics } from './google-analytics';

export class UserProfileService {
    googleAnalytics = new GoogleAnalytics();
    UPDATE_THROTTLE = 1000;
    SYNC_INTERVAL = 20000;
    uid: string;
    onProfileChanged: any;
    db: any;
    cachedProfileDetails = {};
    userServiceClient = new UserServiceClient(getApiConfig(), fetch, console.log, new AuthTokenBuilder(), this.googleAnalytics);
    bookingServiceClient = new BookingServiceClient(getApiConfig(), fetch, new AuthTokenBuilder(), this.googleAnalytics);
    isUpdating = false;

    currentUserProfile: any;
    newUserProfile: any;

    constructor(authProvider, onProfileChanged, setIsReady) {
        this.uid = authProvider.getFirebaseUserId();
        this.authProvider = authProvider;
        this.onProfileChanged = onProfileChanged;
        this.setIsReady = setIsReady;
        this.googleAnalytics.setUserId(this.uid);
    }

    async init(userProfile: UserProfile) {
        this.currentUserProfile = userProfile;
        this.newUserProfile = userProfile;
        this.setIsReady(true);
        this.setIsUpdating(false);
    }

    updateUserProfileAsync = async (newUserProfile: Object) => {
        this.newUserProfile = newUserProfile;
        if (this.isUpdating) {
            console.log('busy updating, wait for current one to complete');
            await this.publishCurrentUserProfile();
            return newUserProfile;
        }

        this.setIsUpdating(true);
        try {
            const updatedProfile = await this.userServiceClient.updateUser(newUserProfile);
            this.setIsUpdating(false);

            this.currentUserProfile = updatedProfile;
            // Update cached userProfile
            Storage.set('userProfile', JSON.stringify(this.newUserProfile));

            // revision not needed at this point anymore
            delete this.currentUserProfile._rev;
            delete this.newUserProfile._rev;

            if (JSON.stringify(this.currentUserProfile) !== JSON.stringify(this.newUserProfile)) {
                setTimeout(() => {
                    this.updateUserProfileAsync(this.newUserProfile);
                }, this.UPDATE_THROTTLE);
                return;
            }
            await this.publishCurrentUserProfile();

            return newUserProfile;
        } catch (error) {
            this.handleFailedSync();
            throw error;
        } finally {
            this.setIsUpdating(false);
        }
    }

    updateUserProfile = async (newUserProfile, updatedAtOnboardingComplete) => {
        try {
            this.setIsUpdating(true);
            const updatedProfile = await this.userServiceClient.updateUser(newUserProfile, updatedAtOnboardingComplete);

            this.currentUserProfile = updatedProfile;
            this.newUserProfile = this.currentUserProfile;
            await this.publishCurrentUserProfile();
            return this.currentUserProfile;
        } catch (e) {
            this.handleFailedSync();
            throw e;
        } finally {
            this.setIsUpdating(false);
        }
    }

    handleFailedSync = async () => {
        this.setIsUpdating(true);
        try {
            this.currentUserProfile = await this.getUserProfile();
            this.newUserProfile = this.currentUserProfile;
            await this.publishCurrentUserProfile();
        } catch (e) {
        } finally {
            this.setIsUpdating(false);
        }
    }

    markNotificationAsRead = (notifiationId: number) => {
        return this.getUserProfileUpdater((userProfile: UserProfile) => {
            const notifications: UserNotification = userProfile.notifications || [];
            userProfile.notifications = notifications.map((notification: UserNotification) => {
                if (notification.id === notifiationId) {
                    notification.read = true;
                }

                return notification;
            });
        }, this.updateUserProfile);
    }

    markNotificationsOfTypeAsRead = (notificationType) => {
        return this.getUserProfileUpdater((userProfile: UserProfile) => {
            const notifications: UserNotification = userProfile.notifications || [];
            userProfile.notifications = notifications.map((notification: UserNotification) => {
                if (notification.type === notificationType && !notification.read) {
                    notification.read = true;
                }

                return notification;
            });
        }, this.updateUserProfile);
    }

    getUserProfileUpdater = async (objectChangeFunction: (UserProfile) => void, updateFunction: (UserProfile) => void) => {
        try {
            const userProfileCopy = Object.assign({}, this.currentUserProfile);
            objectChangeFunction(userProfileCopy);
            return await updateFunction(userProfileCopy);
        } catch (e) {
            console.log(e);
            throw e;
        }
    }

    syncData = async () => {
        if (!this.uid) {
            return;
        }

        if (this.isUpdating) {
            console.log('ignoring sync, busy with update');
            return;
        }

        const latestUserProfile = await this.getUserProfile();
        if (JSON.stringify(latestUserProfile) !== JSON.stringify(this.currentUserProfile)) {
            console.log('user profile changed, updating');
            this.currentUserProfile = latestUserProfile;
            this.newUserProfile = Object.assign({}, latestUserProfile);
            await this.publishCurrentUserProfile();
        }
    }

    async publishCurrentUserProfile() {
        await this.onProfileChanged(this.newUserProfile);
    }

    async registerCurrentDevice(token: string) {
        const device = {
            platform: Platform.OS,
            token,
        };
        console.log('UserProfileService: registering device', device);

        function addDeviceAndSortArray(devices) {
            devices = devices.filter((d) => d.token != token);
            devices.unshift(device);
            return devices;
        }

        return this.getUserProfileUpdater((userProfile: UserProfile) => {
            userProfile.devices = addDeviceAndSortArray(userProfile.devices || []);
        }, this.updateUserProfile);
    }

    getUserProfile = async () => {
        try {
            this.setIsUpdating(true);
            return await this.userServiceClient.getAuthenticatedUser();
        } finally {
            this.setIsUpdating(false);
        }
    }

    refreshCurrentUserProfile = async () => {
        try {
            const userProfile = await this.getUserProfile();
            this.currentUserProfile = userProfile;
            this.newUserProfile = userProfile;
            await this.publishCurrentUserProfile();
        } finally {
            this.setIsUpdating(false);
        }
    }

    getUserProfileDetails = async (userId = this.uid) => {
      const cachedProfile = this.cachedProfileDetails[userId];
      if (cachedProfile) {
        return cachedProfile;
      }

      const request = userId === this.uid ?
        this.userServiceClient.getAuthenticatedUser() : this.userServiceClient.getOtherUser(userId);
      const result = await request;

      this.cacheUserProfile(result);
      return result;
    }

    searchPartner = async (partnerIdentifer, workoutType, workoutStartTime, gymName) => {
      const responseJson = await this.bookingServiceClient.findPartnerForBooking(
        partnerIdentifer, workoutType, workoutStartTime, gymName);
      return responseJson;
    }

    setUserProfilePrivacySetting = async (setting) => {
        const result = await this.userServiceClient.setProfileAsPrivate(setting);
        this.newUserProfile = result;
        await this.publishCurrentUserProfile();
        return result;
    }

    removeFromWhitelist = async (partnerId) => {
      // this does not seem to be used (partners are not simply removed from here, they are added to blacklist)
      const responseJson = await this.userServiceClient.removePartnerFromWhitelist(partnerId);

      this.newUserProfile = responseJson;
      await this.publishCurrentUserProfile();
      return responseJson;
    }

    blockToBlacklist = async (partnerId) => {
      const responseJson = await this.userServiceClient.addPartnerToBlacklist(partnerId);

      this.newUserProfile = responseJson;
      await this.publishCurrentUserProfile();
      return responseJson;
    }

    unblockFromBlacklist = async (partnerId) => {
      const responseJson = await this.userServiceClient.removePartnerFromBlacklist(partnerId);

      this.newUserProfile = responseJson;
      await this.publishCurrentUserProfile();
      return responseJson;
    }

    searchPrivacyPartners = async (partnerIdentifier) => {
      const responseJson = await this.userServiceClient.searchForUserToAddToWhitelist(partnerIdentifier);
      return responseJson;
    }

    addPartnerToWhitelist = async (partnerId) => {
      const responseJson = await this.userServiceClient.addPartnerToWhitelist(partnerId);

      this.newUserProfile = responseJson;
      await this.publishCurrentUserProfile();
      return responseJson;
    }

    getAvailabilityForWhitelistPartners = async (workoutType, workoutStartTime, gymName) => {
      const responseJson = await this.bookingServiceClient.findWhitelistedPartnerForBooking(
        workoutType, workoutStartTime, gymName);
      return responseJson;
    }

    getMyBodyScans = async () => {
        const responseJson = await this.userServiceClient.getMyBodyScans();
        return responseJson;
    }

    getBodyScanPdf = async (scanId) => {
        const responseJson = await this.userServiceClient.getBodyScanPdf(scanId);
        return responseJson;
    }

    cacheUserProfile = (userProfile) => {
        this.cachedProfileDetails[userProfile.uid] = {
            uid: userProfile.uid,
            fullname: userProfile.fullname,
            nickname: userProfile.nickname,
            firstname: userProfile.firstname,
            gender: userProfile.gender,
        };
    }

    createSubscription = (uid, planId, coupon, pendingPlanId, pendingCoupon, stripeCustomerId) => {
      return this.userServiceClient.createSubscription(uid, planId, coupon, pendingPlanId, pendingCoupon, stripeCustomerId);
    }

    setIsUpdating = (value) => {
        this.isUpdating = value;
    }

    async logout() {
        // Storage.clear();
    }

    updateUserHeartRateMonitors = async (heartRateMonitors) => {
        this.newUserProfile = await this.userServiceClient.updateUserHeartRateMonitors(heartRateMonitors);
        this.currentUserProfile = this.newUserProfile;
        await this.publishCurrentUserProfile();
        return this.newUserProfile;
    }
}
